import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Spinner } from 'reactstrap';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { Transactions } from '../../services';
import {
    integrationEventStatusBadgeColor,
    integrationEventKindLabel,
    integrationEventStatusLabel,
    formatIntegrationEventDateTime,
    urlWithoutQuery,
    integrationEventPrimaryTitle,
} from '../../utils/integrationEventLabels';
import './TransactionIntegrationActivity.css';

const TransactionIntegrationActivity = ({ transactionId }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!transactionId) {
            setItems([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        Transactions.getIntegrationActivity(transactionId)
            .then((json) => {
                if (!cancelled) {
                    setItems(Array.isArray(json.integration_activity) ? json.integration_activity : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message || t('transactions.activity_load_error'));
                    setItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [transactionId, t]);

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner size="sm" color="secondary" />{' '}
                <span className="text-muted small">{t('transactions.activity_loading')}</span>
            </div>
        );
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    if (!items.length) {
        return (
            <p className="text-muted small mb-0">{t('transactions.activity_empty')}</p>
        );
    }

    return (
        <div className="TransactionIntegrationActivity">
            <ul className="TransactionIntegrationActivity__timeline">
                {items.map((ev) => (
                    <li key={ev.id} className="TransactionIntegrationActivity__item">
                        <span
                            className={`TransactionIntegrationActivity__dot TransactionIntegrationActivity__dot--${integrationEventStatusBadgeColor(
                                ev.status,
                            )}`}
                            aria-hidden
                        />
                        <div className="TransactionIntegrationActivity__title text-monospace">
                            {integrationEventPrimaryTitle(ev)}
                        </div>
                        <div className="TransactionIntegrationActivity__meta text-muted">
                            {formatIntegrationEventDateTime(ev.occurred_at)}
                            {ev.name ? (
                                <>
                                    {' · '}
                                    <code className="small">{ev.name}</code>
                                </>
                            ) : null}
                            {ev.user_display_name ? (
                                <>
                                    {' · '}
                                    {ev.user_display_name}
                                </>
                            ) : null}
                        </div>
                        <div className="mt-1">
                            <Badge color="info" className="me-1">
                                {integrationEventKindLabel(ev.kind)}
                            </Badge>
                            <Badge color={integrationEventStatusBadgeColor(ev.status)}>
                                {integrationEventStatusLabel(ev.status)}
                            </Badge>
                        </div>
                        {(ev.deliveries || []).length > 0 && (
                            <ul className="TransactionIntegrationActivity__deliveries">
                                {(ev.deliveries || []).map((d) => (
                                    <li key={d.id} className="TransactionIntegrationActivity__delivery">
                                        <span className="text-monospace">{d.http_method}</span>{' '}
                                        {d.direction === 'inbound' || ev.kind === 'psp_webhook'
                                            ? d.url
                                            : urlWithoutQuery(d.url)}{' '}
                                        <span className="text-monospace">
                                            → {d.http_status != null ? d.http_status : '—'}
                                        </span>
                                        {d.duration_ms != null ? (
                                            <span> ({d.duration_ms} ms)</span>
                                        ) : null}
                                        {d.error_message ? (
                                            <div className="text-danger small mt-1">{d.error_message}</div>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default TransactionIntegrationActivity;
